import { Link } from 'react-router-dom';
import { useExpenses } from '../contexts/ExpenseContext';
import { CATEGORIES, MONTHS } from '../types';
import { 
  FileText, 
  Camera, 
  ArrowRight,
  Loader2,
  HardDrive,
  TrendingUp,
  TrendingDown,
  Wallet,
  CalendarDays
} from 'lucide-react';

// Categories that count as INCOME (money coming in)
const INCOME_CATEGORIES = ['settlements'];

// Categories that count as EXPENSES (money going out)  
const EXPENSE_CATEGORIES = ['travel', 'fuel', 'meals', 'office', 'miscellaneous'];

export default function Dashboard() {
  const { expenses, stats, isLoading } = useExpenses();

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  // Filter for current year
  const thisYearExpenses = expenses.filter(e => e.year === currentYear);
  
  // Filter for current month
  const thisMonthExpenses = expenses.filter(
    e => e.month === currentMonth && e.year === currentYear
  );

  // Calculate INCOME (settlements)
  const yearlyIncome = thisYearExpenses
    .filter(e => INCOME_CATEGORIES.includes(e.category))
    .reduce((sum, e) => sum + (e.amount || 0), 0);

  const monthlyIncome = thisMonthExpenses
    .filter(e => INCOME_CATEGORIES.includes(e.category))
    .reduce((sum, e) => sum + (e.amount || 0), 0);

  // Calculate EXPENSES (travel, fuel, meals, etc.)
  const yearlyExpenses = thisYearExpenses
    .filter(e => EXPENSE_CATEGORIES.includes(e.category))
    .reduce((sum, e) => sum + (e.amount || 0), 0);

  const monthlyExpenses = thisMonthExpenses
    .filter(e => EXPENSE_CATEGORIES.includes(e.category))
    .reduce((sum, e) => sum + (e.amount || 0), 0);

  // Calculate NET PROFIT
  const yearlyNetProfit = yearlyIncome - yearlyExpenses;

  // Calculate yearly totals by category  
  const categoryYearlyTotals = CATEGORIES.reduce((acc, cat) => {
    const categoryExpenses = thisYearExpenses.filter(e => e.category === cat.value);
    acc[cat.value] = {
      count: categoryExpenses.length,
      amount: categoryExpenses.reduce((sum, e) => sum + (e.amount || 0), 0)
    };
    return acc;
  }, {} as Record<string, { count: number; amount: number }>);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">
            Tax Expense Tracker
          </h1>
          <p className="text-shuttle-sky mt-1">
            {MONTHS[currentMonth - 1]} {currentYear} • {thisMonthExpenses.length} documents this month
          </p>
        </div>
        <Link
          to="/scanner"
          className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all shadow-lg"
        >
          <Camera className="w-5 h-5" />
          <span>Scan Receipt</span>
        </Link>
      </div>

      {/* Year-to-Date Financial Summary */}
      <div className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Wallet className="w-5 h-5" />
          {currentYear} Financial Summary
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Income */}
          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-green-300/70 uppercase tracking-wide">Income (Settlements)</p>
                <p className="text-2xl font-bold text-green-400">
                  ${yearlyIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          {/* Expenses */}
          <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-xs text-red-300/70 uppercase tracking-wide">Expenses (Deductible)</p>
                <p className="text-2xl font-bold text-red-400">
                  -${yearlyExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          {/* Net Profit */}
          <div className={`${yearlyNetProfit >= 0 ? 'bg-blue-500/10 border-blue-500/30' : 'bg-orange-500/10 border-orange-500/30'} rounded-xl p-4 border`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${yearlyNetProfit >= 0 ? 'bg-blue-500/20' : 'bg-orange-500/20'} rounded-lg flex items-center justify-center`}>
                <Wallet className={`w-5 h-5 ${yearlyNetProfit >= 0 ? 'text-blue-400' : 'text-orange-400'}`} />
              </div>
              <div>
                <p className={`text-xs ${yearlyNetProfit >= 0 ? 'text-blue-300/70' : 'text-orange-300/70'} uppercase tracking-wide`}>Net Profit (Taxable)</p>
                <p className={`text-2xl font-bold ${yearlyNetProfit >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                  ${yearlyNetProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <CalendarDays className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{thisMonthExpenses.length}</p>
              <p className="text-sm text-white/60">{MONTHS[currentMonth - 1]} Docs</p>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-400">
                ${monthlyIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-white/60">{MONTHS[currentMonth - 1]} Income</p>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-400">
                -${monthlyExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-white/60">{MONTHS[currentMonth - 1]} Expenses</p>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
              <HardDrive className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats?.storageUsedFormatted || '0 KB'}</p>
              <p className="text-sm text-white/60">Storage Used</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Link
          to="/scanner"
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group"
        >
          <Camera className="w-8 h-8 text-shuttle-sand mb-3" />
          <p className="font-medium text-white">Scan Receipt</p>
          <p className="text-sm text-white/60">Use camera</p>
        </Link>

        <Link
          to="/import"
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group"
        >
          <span className="text-3xl mb-2 block">📄</span>
          <p className="font-medium text-white">Import PDF</p>
          <p className="text-sm text-white/60">Settlements</p>
        </Link>

        <Link
          to="/bol-send"
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group"
        >
          <span className="text-3xl mb-2 block">📋</span>
          <p className="font-medium text-white">Send BOL</p>
          <p className="text-sm text-white/60">Email to Bennett</p>
        </Link>

        <Link
          to="/export"
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group"
        >
          <span className="text-3xl mb-2 block">📦</span>
          <p className="font-medium text-white">Export PDF</p>
          <p className="text-sm text-white/60">For CPA</p>
        </Link>
      </div>

      {/* Year-to-Date by Category */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-white">{currentYear} by Category</h2>
            <p className="text-xs text-white/50">Year-to-date totals</p>
          </div>
          <Link 
            to="/expenses" 
            className="text-sm text-shuttle-sky hover:text-white flex items-center space-x-1"
          >
            <span>View all</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CATEGORIES.map((cat) => {
            const yearData = categoryYearlyTotals[cat.value] || { count: 0, amount: 0 };
            const isIncome = INCOME_CATEGORIES.includes(cat.value);
            return (
              <Link
                key={cat.value}
                to={`/expenses?category=${cat.value}`}
                className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/20 transition-all group"
              >
                <div className="text-3xl mb-2">{cat.icon}</div>
                <p className="text-2xl font-bold text-white">{yearData.count}</p>
                <p className="text-sm text-white/60 truncate">{cat.label}</p>
                {yearData.amount > 0 && (
                  <p className={`text-sm font-medium mt-1 ${isIncome ? 'text-green-400' : 'text-red-400'}`}>
                    {isIncome ? '+' : '-'}${yearData.amount.toFixed(2)}
                  </p>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent Documents */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Recent Documents</h2>
        </div>
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
          {expenses.length > 0 ? (
            <div className="divide-y divide-white/10">
              {expenses.slice(0, 5).map((expense) => {
                const category = CATEGORIES.find(c => c.value === expense.category);
                const isIncome = INCOME_CATEGORIES.includes(expense.category);
                return (
                  <div
                    key={expense.id}
                    className="flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <span className="text-2xl flex-shrink-0">{category?.icon || '📄'}</span>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-white truncate text-sm">
                          {expense.filename}
                        </p>
                        <p className="text-xs text-white/60 truncate">
                          {expense.description || category?.label} • {new Date(expense.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <span className={`category-badge category-${expense.category} text-xs`}>
                        {isIncome ? 'Income' : category?.label}
                      </span>
                      {expense.amount && (
                        <span className={`font-medium text-sm ${isIncome ? 'text-green-400' : 'text-red-400'}`}>
                          {isIncome ? '+' : '-'}${expense.amount.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center text-white/60">
              <FileText className="w-12 h-12 mx-auto mb-4 text-white/30" />
              <p>No documents yet</p>
              <Link
                to="/scanner"
                className="inline-flex items-center space-x-2 mt-4 text-shuttle-sky hover:text-white"
              >
                <Camera className="w-4 h-4" />
                <span>Scan your first receipt</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
